import { Collapse, Input, Modal, Spin, Switch, Tooltip } from 'antd';

const { Panel } = Collapse;
import React, { FC, useContext, useEffect, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { DateRangePicker } from '../DateRangePicker';
import SingleDatePicker from '../SingleDatePicker';
import { CustomSelect } from '../DimSection';
import styled from '@emotion/styled';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { generateErrorMsg, getAccessIcon, getConfig, sendNotification } from 'utils/helperMethods';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingSimulatorUrls } from 'config/urls';
import dayjs from 'dayjs';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import { queryClient } from 'services/reactQueryService';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import isBetween from 'dayjs/plugin/isBetween';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

dayjs.extend(isBetween);
type Props = {
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
    isEdit: boolean;
    editParams: any;
};

const ppaScenarioList = [
    { label: 'PPA National 2026.PPA 2026', value: 'ppaNational2026Ppa2026' },
    { label: 'PPA National 2025.PPA 2026 v2', value: 'ppaNational2025Ppa2026V2' },
    { label: 'PPA National 2025.PPA 2026 v3', value: 'ppaNational2025Ppa2026V3' },
];

export const CustomModal = styled(Modal)`
    .ant-modal-content {
        border-radius: 8px !important;
    }
    .ant-modal-header {
        border-radius: 8px 8px 0px 0px !important;
        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const constantWidth = 300;
const characterLimit = 7;
const selectAllItem = { label: 'Select All', value: 'selectAll' };

const CreateEditProjectContainer: FC<Props> = ({ onCancel, open, onSubmitFinish, isEdit, editParams }) => {
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const pricingSimulator = getConfig('pricingSimulator', appConfig);

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const dim1Attribute = dimensionConfigData?.dim1Attribute;
    const dim2Attribute = dimensionConfigData?.dim2Attribute;
    const dim1NestedAttribute = dimensionConfigData?.dim1NestedAttribute;

    const { projectDataSource, simulatedPeriodItems, enablePrivatePublicToggle, hidePriceEffectiveDateProperty } =
        pricingSimulator;

    //create project states
    const [projectName, setProjectName] = useState('');
    const [isPrivate, setIsPrivate] = useState(enablePrivatePublicToggle ? true : false);
    const [tempSimulationTimePeriodSelection, setTempSimulationTimePeriodSelection] = useState<[any, any]>([
        null,
        null,
    ]);
    const [simulationTimePeriodSelection, setSimulationTimePeriodSelection] = useState<any>();
    const [priceEffectiveDate, setPriceEffectiveDate] = useState<[any, any]>([null, null]);
    const [tempPriceEffectiveDate, setTempPriceEffectiveDate] = useState<[any, any]>([null, null]);
    const [dataSource, setDataSource] = useState<any>();
    const [ppaScenarioSelected, setPpaScenarioSelected] = useState<any>();
    const [tradeScenarioSelected, setTradeScenarioSelected] = useState<any>();
    const [nutrifuelVolumeSales, setNutrifuelVolumeSales] = useState<string>('');
    const [nutrifuelValueSales, setNutrifuelValueSales] = useState<string>('');
    const [nutrifuelProfitAbs, setNutrifuelProfitAbs] = useState<string>('');
    const [nutrifuelMargin, setNutrifuelMargin] = useState<string>('');
    const [nutrifuelTradeRate, setNutrifuelTradeRate] = useState<string>('');
    const [simulationPeriodDropdown, setSimulationPeriodDropdown] = useState<any>();
    const [dim1Attributes, setDim1Attributes] = useState<any>([]);
    const [dim2Attributes, setDim2Attributes] = useState<any>([]);
    const [dim1NestedAttributes, setDim1NestedAttributes] = useState<any>([]);
    // const [allowCompetitors, setAllowCompetitors] = useState(false);

    const getDim1AttributesVariable = {
        project_id: editParams?.id || null,
        attribute_name: dim1Attribute?.key,
        dimension: dim1Attribute?.dimension,
        order_by_name: dim1Attribute?.orderByName,
    };

    //get attribute types - dim 1  - category
    const {
        data: dim1List,
        loading: dim1ListLoading,
        error: dim1ListError,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim1Attribute) },
        queryKey: ['ps-get-attribute-types', 'ps-get-attribute-types-dim1', getDim1AttributesVariable],
        url: `${pricingSimulatorUrls.getProjectFiltersUrl}`,
        variables: { ...getDim1AttributesVariable },
        parserFunction: (res: any) => {
            if (res?.values?.length === 0) {
                throw 'No Data';
            }

            const tickedAttributes: any[] = [];
            const result = res?.values?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push(item?.value);
                }
                return {
                    label: item?.label,
                    value: item?.value,
                };
            });
            setDim1Attributes(tickedAttributes);
            return result;
        },
    });
    const getDim1NestedAttributesVariable = {
        project_id: editParams?.id || null,
        attribute_name: dim1NestedAttribute?.key,
        dimension: dim1NestedAttribute?.dimension,
        order_by_name: dim1NestedAttribute?.orderByName,
    };
    //get attribute types - dim 1 nested  - manufacturer
    const {
        data: dim1NestedList,
        loading: dim1NestedListLoading,
        error: dim1NestedListError,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim1NestedAttribute) && Boolean(dim1Attributes?.length > 0) },
        queryKey: ['ps-get-attribute-types', 'ps-get-attribute-types-dim1-nested', getDim1NestedAttributesVariable],
        url: `${pricingSimulatorUrls.getProjectFiltersUrl}`,
        variables: { ...getDim1NestedAttributesVariable },
        parserFunction: (res: any) => {
            if (res?.values?.length === 0) {
                throw 'No Data';
            }
            if (Array.isArray(res?.values) && res.values.length > 0) {
                const tickedAttributes: any[] = [];
                const result = res?.values?.map((item: any) => {
                    if (item?.ticked) {
                        tickedAttributes.push(item?.value);
                    }
                    return {
                        label: item?.label,
                        value: item?.value,
                    };
                });
                //check select all if all are selected
                if (tickedAttributes?.length > 0 && tickedAttributes?.length === res.values?.length) {
                    tickedAttributes.unshift(selectAllItem.value);
                }
                setDim1NestedAttributes(tickedAttributes);
                return [selectAllItem, ...result];
            }
            return [];
        },
    });

    const getDim2AttributesVariable = {
        project_id: editParams?.id || null,
        attribute_name: dim2Attribute?.key,
        dimension: dim2Attribute?.dimension,
        order_by_name: dim2Attribute?.orderByName,
    };
    //get attribute types - dim 2  - BU
    const {
        data: dim2List,
        loading: dim2ListLoading,
        error: dim2ListError,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim2Attribute) },
        queryKey: ['ps-get-attribute-types', 'ps-get-attribute-types-dim2', getDim2AttributesVariable],
        url: `${pricingSimulatorUrls.getProjectFiltersUrl}`,
        variables: { ...getDim2AttributesVariable },
        parserFunction: (res: any) => {
            if (res?.values?.length === 0) {
                throw 'No Data';
            }
            const tickedAttributes: any[] = [];
            const result = res?.values?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push(item?.value);
                }
                return {
                    label: item?.label,
                    value: item?.value,
                };
            });
            setDim2Attributes(tickedAttributes);
            return result;
        },
    });

    //create project api
    const { mutate: createEditProjectFn, loading: createProjectLoading } = useCommonMutationHandler({
        url: isEdit ? pricingSimulatorUrls.updateProjectUrl : pricingSimulatorUrls.createProjectUrl,
        method: isEdit ? 'put' : 'post',
    });

    function resetAllFields() {
        setProjectName('');
        setTempSimulationTimePeriodSelection([null, null]);
        setSimulationTimePeriodSelection(undefined);
        setPriceEffectiveDate([null, null]);
        setTempPriceEffectiveDate([null, null]);
        setDataSource(undefined);
        setSimulationPeriodDropdown(undefined);
        setDim1Attributes([]);
        setDim2Attributes([]);
        setDim1NestedAttributes([]);
        setIsPrivate(enablePrivatePublicToggle ? true : false);
        setPpaScenarioSelected(undefined);
        setTradeScenarioSelected(undefined);
        setNutrifuelVolumeSales('');
        setNutrifuelValueSales('');
        setNutrifuelProfitAbs('');
        setNutrifuelMargin('');
        setNutrifuelTradeRate('');
        // setAllowCompetitors(false);
    }
    //reset all fields to default when popup closes
    useEffect(() => {
        if (!open) {
            resetAllFields();

            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-get-attribute-types'],
            });
        }
        //if coming from edit, then pre populate data
        if (isEdit && Object.keys(editParams || {}).length > 0) {
            setProjectName(editParams?.projectName);
            setSimulationTimePeriodSelection(editParams?.simulationTimePeriodSelection);
            setPriceEffectiveDate(editParams?.priceEffectiveDate);
            setTempPriceEffectiveDate(editParams?.priceEffectiveDate);
            // setAllowCompetitors(editParams?.allowCompetitors);
            setDataSource(editParams?.dataSource);
            setSimulationPeriodDropdown(editParams?.simulatedPeriodDropdownItem);
            setTempSimulationTimePeriodSelection(editParams?.tempSimulationTimePeriodSelection);
            setIsPrivate(editParams?.isPrivate ?? true);
        }
    }, [open]);

    //checking if any fields are missing
    //if any fields are missing, dont enable submit button
    function validateFields() {
        if (!projectName?.trim()) return true;
        if (!simulationTimePeriodSelection) return true;
        // if (priceEffectiveDate[1] === null) return true; // optional field
        if (!dataSource) return true;
        if (dim1Attributes?.length === 0) return true;
        if (dim1NestedAttributes?.length === 0) return true;
        if (dim2Attribute && dim2Attributes?.length === 0) return true;
        return false;
    }

    //checking is effective date is between simulation date
    function isEffectiveDateBetweenSimulationDate(simulationDates?: [any, any], effectiveDates?: [any, any]) {
        const tempSimulationDates = simulationDates ?? tempSimulationTimePeriodSelection;
        const tempEffectiveDates = effectiveDates ?? tempPriceEffectiveDate;
        if (
            !dayjs(tempEffectiveDates?.[1]).isBetween(
                tempSimulationDates?.[0],
                tempSimulationDates?.[1],
                undefined,
                '[]',
            )
        ) {
            sendNotification('error', PricingSimulatorNotificationMessages.PRICE_EFFECTIVE_DATE_ERROR);
            setPriceEffectiveDate([null, null]);
            setTempPriceEffectiveDate([null, null]);
            return false;
        }
        return true;
    }

    const onSubmit = () => {
        if (createProjectLoading) return;
        const dim1Variable = {
            [dim1Attribute?.key ?? '']: dim1Attributes,
            [dim1NestedAttribute?.key ?? '']: dim1NestedAttributes?.filter(
                (attr: any) => selectAllItem?.value !== attr,
            ),
        };
        let dim2Variable = {};
        if (dim2Attribute) {
            dim2Variable = {
                dim2: {
                    [dim2Attribute?.key]: dim2Attributes,
                },
            };
        }

        const utcOffset = dayjs().utcOffset();
        const formattedPriceEffectiveDate = priceEffectiveDate?.[1]
            ? dayjs(priceEffectiveDate?.[1]).add(utcOffset, 'm').subtract(6, 'day').unix()
            : null;
        const payload: any = {
            project_name: projectName?.trim(),
            is_private: isPrivate,
            created_by: localStorage.getItem('email'),
            simulation_time_period: {
                start_timestamp: JSON.parse(simulationTimePeriodSelection || '{}')?.start_timestamp,
                end_timestamp: JSON.parse(simulationTimePeriodSelection || '{}')?.end_timestamp,
            },
            simulation_period_type: simulationPeriodDropdown?.value,
            price_effective_date: formattedPriceEffectiveDate,
            data_source: dataSource?.value,
            project_filters: {
                dim1: dim1Variable,
                ...dim2Variable,
            },

            // include_competitors: false, //remove it once backend has made changes
        };
        if (isEdit && editParams?.id) {
            payload.project_id = editParams?.id;
        }
        createEditProjectFn(
            { ...payload },
            {
                onSuccess: (data) => {
                    onSubmitFinish(data?.data?.project_id || null);
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail),
                        PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG,
                    );
                    //show error message
                    sendNotification('error', errorMsg);
                },
            },
        );
    };
    const modalTitle = isEdit ? 'Edit Project' : 'Create Project';
    const modalButtonTitle = isEdit ? 'Save Project' : 'Create Project';
    const isSubmitButtonDisabled = validateFields();
    return (
        <div>
            <CustomModal
                bodyStyle={{ background: colorConstants.greyAlt9 }}
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{ style: { borderRadius: 6 }, disabled: isSubmitButtonDisabled }}
                okText={
                    createProjectLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : modalButtonTitle
                }
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                width={650}
                onCancel={(e) => {
                    if (createProjectLoading) return;
                    onCancel(e);
                }}
                title={modalTitle}
            >
                <div className="flex-col flex gap-6">
                    <div className="flex items-center">
                        <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>Project Name :
                        </p>
                        <Input
                            value={projectName}
                            type="text"
                            onChange={(e) => setProjectName(e.target.value)}
                            style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                            placeholder="Input"
                        />
                    </div>
                    <div className="flex items-center">
                        <p className="text font-xs mr-5 w-[180px] text-right  text-blackAlt">
                            <span className="text-redAlt">* </span>Simulation Period{' '}
                            {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                        </p>
                        <div className="flex gap-3 flex-grow items-center">
                            <CustomSelect
                                className="w-40"
                                value={simulationPeriodDropdown?.value}
                                placeholder="Select"
                                onChange={(val, item: any) => {
                                    //reset fields
                                    if (val === 'custom_date') {
                                        setTempSimulationTimePeriodSelection([null, null]);
                                        setSimulationTimePeriodSelection(undefined);

                                        //for financial year and current year
                                    } else {
                                        setTempSimulationTimePeriodSelection([
                                            item?.dateArray?.[0],
                                            item?.dateArray?.[1],
                                        ]);
                                        const utcOffset = dayjs().utcOffset();

                                        const startDateUnix = dayjs(item?.dateArray?.[0])
                                            .add(utcOffset, 'm')
                                            .subtract(6, 'day')
                                            .unix();
                                        const endDateUnix = dayjs(item?.dateArray?.[1])
                                            .add(utcOffset, 'm')
                                            .subtract(6, 'day')
                                            .unix();
                                        setSimulationTimePeriodSelection(
                                            JSON.stringify({
                                                start_timestamp: startDateUnix,
                                                end_timestamp: endDateUnix,
                                            }),
                                        );
                                        //check if effective date is between simulation period
                                        isEffectiveDateBetweenSimulationDate([
                                            item?.dateArray?.[0],
                                            item?.dateArray?.[1],
                                        ]);
                                    }
                                    setSimulationPeriodDropdown(item);
                                }}
                                options={simulatedPeriodItems}
                            />
                            {['financial_year', 'current_year'].includes(simulationPeriodDropdown?.value) && (
                                <p className="font-xs">
                                    {dayjs(tempSimulationTimePeriodSelection?.[0]).format(commonConfig?.dateFormatAlt)}{' '}
                                    -{' '}
                                    {dayjs(tempSimulationTimePeriodSelection?.[1]).format(commonConfig?.dateFormatAlt)}
                                </p>
                            )}
                            {simulationPeriodDropdown?.value === 'custom_date' && (
                                <DateRangePicker
                                    isEffectiveDateBetweenSimulationDate={isEffectiveDateBetweenSimulationDate}
                                    simulationDateRange={tempSimulationTimePeriodSelection}
                                    setSimulationDateRange={setTempSimulationTimePeriodSelection}
                                    setSimulationTimePeriodSelection={setSimulationTimePeriodSelection}
                                    simulationTimePeriodSelection={simulationTimePeriodSelection}
                                />
                            )}
                        </div>
                    </div>
                    {!hidePriceEffectiveDateProperty && (
                        <div className="flex items-center">
                            <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                {/* <span className="text-redAlt">* </span> */}
                                Price Effective Date
                                {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                            </p>
                            <SingleDatePicker
                                simulationDateRange={tempSimulationTimePeriodSelection}
                                isEffectiveDateBetweenSimulationDate={isEffectiveDateBetweenSimulationDate}
                                priceEffectiveDate={priceEffectiveDate}
                                setPriceEffectiveDate={setPriceEffectiveDate}
                                setTempPriceEffectiveDate={setTempPriceEffectiveDate}
                                tempPriceEffectiveDate={tempPriceEffectiveDate}
                            />
                        </div>
                    )}
                    <div className="flex items-center">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>Data Source{' '}
                            {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                        </p>
                        <CustomSelect
                            style={{ width: constantWidth }}
                            value={dataSource?.value}
                            placeholder="Select"
                            onChange={(val, item) => setDataSource(item)}
                            options={projectDataSource}
                        />
                    </div>
                    <div className="flex items-center">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>
                            {dim1Attribute?.label}(s){' '}
                            {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                        </p>
                        <CustomSelect
                            loading={dim1ListLoading}
                            disabled={dim1ListLoading}
                            style={{ width: constantWidth }}
                            mode="multiple"
                            placeholder="Multiple Select"
                            maxTagCount={2}
                            maxTagPlaceholder={(val) => {
                                const remainingCount = val.length || 0;
                                return <MaxTagComponent remainingCount={remainingCount} />;
                            }}
                            showArrow
                            showSearch={false}
                            maxTagTextLength={characterLimit}
                            value={dim1Attributes}
                            onChange={(val, item) => {
                                setDim1NestedAttributes([]);
                                setDim1Attributes(val);
                            }}
                            options={dim1List}
                        />
                    </div>

                    <div className="flex items-baseline">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>
                            {dim1NestedAttribute?.label}(s){' '}
                            {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                        </p>
                        <div
                            title={
                                dim1Attributes?.length === 0
                                    ? `Please select at least one ${dim1Attribute?.label} to enable this field`
                                    : ''
                            }
                        >
                            <CustomSelect
                                loading={dim1NestedListLoading}
                                disabled={dim1NestedListLoading || dim1Attributes?.length === 0}
                                style={{ width: constantWidth }}
                                mode="multiple"
                                placeholder="Multiple Select"
                                maxTagCount={2}
                                maxTagPlaceholder={(val) => {
                                    const remainingCount = val.length || 0;
                                    return <MaxTagComponent remainingCount={remainingCount} />;
                                }}
                                showArrow
                                showSearch={false}
                                maxTagTextLength={characterLimit}
                                value={dim1NestedAttributes}
                                //using onselect and on deselect here since select all is present

                                onSelect={(val) => {
                                    if (val === selectAllItem.value) {
                                        setDim1NestedAttributes(dim1NestedList?.map((item: any) => item?.value));
                                    } else {
                                        setDim1NestedAttributes([...dim1NestedAttributes, val]);
                                    }
                                }}
                                onDeselect={(val) => {
                                    if (val === selectAllItem.value) {
                                        setDim1NestedAttributes([]);
                                    } else {
                                        setDim1NestedAttributes(
                                            dim1NestedAttributes?.filter(
                                                (item: any) => ![selectAllItem.value, val].includes(item),
                                            ),
                                        );
                                    }
                                }}
                                // onChange={(val: any, item) => {
                                //     if (val?.includes(selectAllItem.value)) {
                                //         setDim1NestedAttributes(dim1NestedList?.map((item) => item?.value));
                                //     } else setDim1NestedAttributes(val);
                                // }}
                                options={dim1NestedList}
                            />

                            {/* <div className="ml-2 mt-6">
                                <Switch
                                    disabled={dim1Attributes?.length === 0}
                                    checked={allowCompetitors}
                                    onChange={(val) => setAllowCompetitors(val)}
                                />{' '}
                                <span className="ml-2 font-xs text-blackAlt">Include Competitors</span>
                            </div> */}
                        </div>
                    </div>
                    {dim2Attribute && (
                        <div className="flex items-center">
                            <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                <span className="text-redAlt">* </span>
                                {dim2Attribute?.label}(s){' '}
                                {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                            </p>
                            <CustomSelect
                                style={{ width: constantWidth }}
                                mode="multiple"
                                placeholder="Multiple Select"
                                maxTagCount={2}
                                maxTagPlaceholder={(val) => {
                                    const remainingCount = val.length || 0;
                                    return <MaxTagComponent remainingCount={remainingCount} />;
                                }}
                                showArrow
                                showSearch={false}
                                maxTagTextLength={characterLimit}
                                value={dim2Attributes}
                                onChange={(val, item) => setDim2Attributes(val)}
                                options={dim2List}
                                loading={dim2ListLoading}
                                disabled={dim2ListLoading}
                            />
                        </div>
                    )}
                    {enablePrivatePublicToggle && (
                        <div className="flex items-center">
                            <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                <span className="text-redAlt">* </span>
                                Project Visibility
                                <Tooltip
                                    color="black"
                                    title="Once a project is made public and saved, you won’t be able to revert it back to private."
                                    placement="top"
                                    trigger={'hover'}
                                >
                                    <InfoCircleOutlined
                                        style={{ cursor: 'pointer', position: 'relative', marginLeft: 4, top: -3 }}
                                    />{' '}
                                    :
                                </Tooltip>
                            </p>
                            <div className="">
                                <span className="mr-2 font-xs text-blackAlt">Public</span>
                                <Switch
                                    disabled={isEdit && !isPrivate}
                                    checked={isPrivate}
                                    onChange={(val) => setIsPrivate(val)}
                                />{' '}
                                {getAccessIcon(true)}
                                <span className="ml-2 font-xs text-blackAlt">Private</span>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col gap-4">
                        <Collapse expandIconPosition="end" bordered={false} style={{ background: 'transparent' }}>
                            <Panel header="Link Scenario" key="1">
                                <div className="flex-col flex gap-3 max-h-[120px] overflow-y-auto">
                                    <div className="flex items-center">
                                        <p className="text w-[180px] mr-4 text-right font-xs text-blackAlt">
                                            <span className="text-redAlt"> </span>
                                            PPA Scenario
                                            {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}
                                            :
                                        </p>
                                        <CustomSelect
                                            style={{ width: constantWidth }}
                                            value={ppaScenarioSelected?.value}
                                            placeholder="Select"
                                            onChange={(val, item) => setPpaScenarioSelected(item)}
                                            options={ppaScenarioList}
                                        />
                                    </div>
                                </div>
                            </Panel>
                        </Collapse>
                        <Collapse expandIconPosition="end" bordered={false} style={{ background: 'transparent' }}>
                            <Panel header="Goal Settings" key="2">
                                <div className="flex-col flex gap-3 max-h-[120px] overflow-y-auto">
                                    <div className="flex items-center">
                                        <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                                            Nutrifuel Volume Sales :
                                        </p>
                                        <Input
                                            value={nutrifuelVolumeSales}
                                            type="number"
                                            onChange={(e) => setNutrifuelVolumeSales(e.target.value)}
                                            style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                                            placeholder="Input"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                                            Nutrifuel Value Sales ($) :
                                        </p>
                                        <Input
                                            value={nutrifuelValueSales}
                                            type="number"
                                            onChange={(e) => setNutrifuelValueSales(e.target.value)}
                                            style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                                            placeholder="Input"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                                            Nutrifuel Profit Abs ($) :
                                        </p>
                                        <Input
                                            value={nutrifuelProfitAbs}
                                            type="number"
                                            onChange={(e) => setNutrifuelProfitAbs(e.target.value)}
                                            style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                                            placeholder="Input"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                                            Nutrifuel Margin (%) :
                                        </p>
                                        <Input
                                            value={nutrifuelMargin}
                                            type="number"
                                            onChange={(e) => setNutrifuelMargin(e.target.value)}
                                            style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                                            placeholder="Input"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                                            Nutrifuel Trade Rate (%) :
                                        </p>
                                        <Input
                                            value={nutrifuelTradeRate}
                                            type="number"
                                            onChange={(e) => setNutrifuelTradeRate(e.target.value)}
                                            style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                                            placeholder="Input"
                                        />
                                    </div>
                                </div>
                            </Panel>
                        </Collapse>
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

export default CreateEditProjectContainer;

export const MaxTagComponent = ({ remainingCount }: { remainingCount: number }) => {
    return <span className="font-xxs">+{remainingCount} more</span>;
};
